import type { TimeGranularity, ChartType } from '../../types';
import { GRANULARITY_LABELS, CHART_CONSTANTS } from '../../constants';

interface GranularityBadgesProps {
  granularity: TimeGranularity;
  chartType: ChartType;
  hasDualAxis: boolean;
  hasComparison: boolean;
  hasMetricSelection: boolean;
  startDate: Date;
  endDate: Date;
}

/**
 * Renders informational badges below the chart.
 * Shows granularity, chart type, dual-axis indicator, and date range.
 */
export function GranularityBadges({
  granularity,
  chartType,
  hasDualAxis,
  hasComparison,
  hasMetricSelection,
  startDate,
  endDate,
}: GranularityBadgesProps) {
  const { COLORS } = CHART_CONSTANTS;

  const formatDateRange = (start: Date, end: Date) => {
    const fmt = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });
    return `${fmt(start)} – ${fmt(end)}`;
  };

  return (
    <div className="flex gap-6 justify-center items-center mt-3 flex-wrap">
      {/* Granularity badge */}
      <span className="text-[0.7rem] py-1 px-2 rounded bg-brand-dark text-brand-primary uppercase font-semibold tracking-wide font-display">
        {GRANULARITY_LABELS[granularity] || 'Por Mês'}
      </span>

      {/* Chart type badge */}
      <span className={`text-[0.7rem] py-1 px-2 rounded uppercase font-semibold tracking-wide font-display
        ${chartType === 'line'
          ? 'bg-brand-blue/20 text-brand-blue'
          : 'bg-success/20 text-success'
        }
      `}>
        {chartType === 'line' ? 'Linha' : 'Barras'}
      </span>

      {/* Dual-axis badge */}
      {hasDualAxis && (
        <span className="text-[0.7rem] py-1 px-2 rounded bg-error/20 text-error uppercase font-semibold tracking-wide font-display">
          ⚖️ Dual Eixo (escalas diferentes)
        </span>
      )}

      {/* Date range (only in simple mode) */}
      {!hasComparison && !hasMetricSelection && (
        <span className="flex items-center gap-2 text-xs text-brand-gray font-body">
          <span
            className="w-3.5 h-3.5 rounded-sm"
            style={{ backgroundColor: COLORS.CURRENT }}
          />
          {formatDateRange(startDate, endDate)}
        </span>
      )}
    </div>
  );
}
